import React, { lazy, memo, Suspense, useMemo } from 'react';

import { Box, Typography } from '@mui/material';
import { useTheme } from '@mui/material/styles';
import clsx from 'clsx';
import _ from 'lodash';
import PropTypes from 'prop-types';

import { FieldInputType } from 'constants/fields';
import { ItemGroupAlignment } from 'constants/ui';
import { getFieldPendingChangeAuthors } from 'utils/pendingChangesAuthors';
import { checkIsLightTheme } from 'utils/theme';

import { loanManagementTestIds } from '../../loanManagement.testIds';

import ContentViewGenerator from './ContentViewGenerator';

// Loaded on first use, so pages without pending-change authors do not pull in the loan's pending-changes store.
const PendingChangeAuthorDots = lazy(() => import('@sharedCR/common/PendingChangeAuthorDots'));
const defaultArray = [];
const defaultObject = {};

const getStyles = ({ palette, breakpoints }) => {
  const isLightTheme = checkIsLightTheme(palette.type);

  return {
    root: {
      '& [data-is-field-changed="true"]': {
        color: palette.primary.main,
        '& .MuiCheckbox-root, & .MuiRadio-root': {
          color: palette.primary.main,
        },
      },
    },
    row: {
      display: 'flex',
      color: palette.text.primary,

      '& > div': {
        padding: '10px 35px',

        '&:not(:last-child):not(.w-full)': {
          borderRight: `1px solid ${isLightTheme ? palette.grey['500'] : palette.primary['300']}`,
        },

        '&:first-child': {
          paddingLeft: 0,
        },

        [breakpoints.down('sm')]: {
          width: '100% !important',
          borderRight: 'none !important',
          padding: '10px !important',
        },
      },

      [breakpoints.down('sm')]: {
        flexDirection: 'column !important',
      },
    },
  };
};

const Content = ({
  config = defaultArray,
  data = null,
  dataChanges = defaultObject,
  dataAuthors = defaultObject,
  dataTestId = null,
}) => {
  const theme = useTheme();
  const classes = useMemo(() => getStyles(theme), [theme]);

  const checkIsFieldHidden = ({ name, type }) => {
    const value = _.get(data, name);
    const isEmpty = !value && !_.isNumber(value);
    const isBooleanField = type === FieldInputType.BOOL;

    return isEmpty && !isBooleanField;
  };

  if (_.isEmpty(data)) {
    return <Typography variant="regular14">No Data</Typography>;
  }

  return (
    <Box data-testid={dataTestId} sx={classes.root}>
      {config.map(
        (
          { name, groups, subtitle, containerClassName, groupsAlignment, containerParting = '1/3' },
          index,
        ) => {
          const isVerticalAligned = groupsAlignment === ItemGroupAlignment.VERTICAL;

          if (!groups.some(group => !group.every(checkIsFieldHidden))) {
            return null;
          }

          return (
            <div key={index}>
              {!!name && (
                <Typography className="mb-16" color="inherit" variant="bold16">
                  {name}
                </Typography>
              )}

              {!!subtitle && (
                <Typography className="mb-16" color="inherit" key={index} variant="medium14">
                  {subtitle}
                </Typography>
              )}

              <Box className={isVerticalAligned ? 'flex-col' : 'flex-row'} sx={classes.row}>
                {groups.map((group, index) => {
                  if (!group.length || group.every(checkIsFieldHidden)) {
                    return null;
                  }

                  return (
                    <div
                      className={clsx(
                        isVerticalAligned ? 'w-full' : `w-${containerParting}`,
                        'content-view__group-container',
                        `content-view__group-container-${name}`,
                        containerClassName,
                      )}
                      data-testid={loanManagementTestIds.getContentViewGroupId(index)}
                      key={index}
                    >
                      {group.map(
                        (
                          {
                            name,
                            contentViewClassName,
                            type,
                            subtitle,
                            component,
                            changesProps,
                            ...restProps
                          },
                          index,
                        ) => {
                          if (checkIsFieldHidden({ name, type }) && !subtitle) {
                            return null;
                          }

                          if (subtitle) {
                            return (
                              <Typography
                                className="mb-16"
                                color="inherit"
                                key={index}
                                variant="medium14"
                              >
                                {subtitle}
                              </Typography>
                            );
                          }

                          const value = _.get(data, name);
                          const isFieldChanged = _.get(dataChanges, name);
                          const fieldAuthors = isFieldChanged
                            ? getFieldPendingChangeAuthors(dataAuthors, name)
                            : null;

                          return (
                            <Box
                              className={clsx('flex mb-8', contentViewClassName)}
                              component={component}
                              data-is-field-changed={isFieldChanged}
                              data-testid={loanManagementTestIds.getContentViewRowId(index)}
                              key={index}
                            >
                              {!_.isEmpty(fieldAuthors) && (
                                <Suspense fallback={null}>
                                  <PendingChangeAuthorDots
                                    authors={fieldAuthors}
                                    fieldLabel={_.isString(restProps.label) ? restProps.label : ''}
                                  />
                                </Suspense>
                              )}
                              <ContentViewGenerator
                                {...restProps}
                                name={name}
                                type={type}
                                value={value}
                              />
                            </Box>
                          );
                        },
                      )}
                    </div>
                  );
                })}
              </Box>
            </div>
          );
        },
      )}
    </Box>
  );
};

Content.propTypes = {
  data: PropTypes.oneOfType([PropTypes.object, PropTypes.array]),
  dataChanges: PropTypes.object,
  dataAuthors: PropTypes.object,
  config: PropTypes.array,
  dataTestId: PropTypes.string,
};

export default memo(Content);
